import React from 'react'
import Modal from './Modal'
import { Flags } from 'react-feature-flags'

export default () => (
    <Modal>
        <div className="methods">
            <header>
                <h2 className="title">Methods</h2>
            </header>
            <h4 className="title is-4">Tool Design and Development</h4>
            <p>
                The SiteCheck tool was designed and implemented by the Conservation Biology Institute (CBI) in close
                collaboration with the Office of Land Use and Climate Innovation (LCI), with feedback from agency staff, planners and
                developers. The tool is designed to work on a variety of devices, from desktop computers to tablets and
                smartphones. The tool was designed and implemented with accessibility in mind. This includes large font
                sizes, high contrast color schemes, and page markup written to make the tool easier to use with screen
                readers and when using keyboard navigation.
            </p>
            <p>
                The tool primarily uses the Python and JavaScript programming languages, with the Django and ReactJS
                frameworks. The mapping functionality is implemented using the Mapbox GL JS library along with tiled,
                vector parcel data in order to support high-performance mapping of the 11+ million parcels in the state
                of California. Spatial data is stored and analyzed using the PostgreSQL database with the PostGIS
                spatial extension.
            </p>

            <h4 className="title is-4">Data Analysis</h4>
            <p>
                Each CEQA exemption or streamlining provision has a set of requirements for which a parcel must qualify
                in order to be eligible for that exemption or provision. Some of these requirements are spatial (e.g.,{' '}
                <em>the parcel must be within city limits</em>) and some are based on project specifications (e.g., the
                project must not have a significant air quality or transportation impact). Using GIS software, each
                parcel in the state of California was analyzed to determine whether or not it meets each of the spatial
                requirements. This determination was based on the spatial relationships each parcel has with other
                spatial datasets pertaining to the requirement.
            </p>

            <ul className="bullets">
                <li>A calculated value of 1 indicates that the parcel meets the requirement.</li>
                <li>A calculated value of 0 indicates that the parcel does not meet the requirement.</li>
                <li>
                    A NULL value indicates that there is not enough information (data) to calculate a 1 or a 0. NULL is
                    only assigned when there is no data available anywhere within the County.
                </li>
            </ul>

            <p>
                The requirement calculations were subsequently used to determine whether or not a parcel may qualify for
                each of the CEQA exemptions or streamlining provisions.
            </p>
            <p>
                The script used to calculate both requirements and exemptions for each parcel is available{' '}
                <a
                    href="https://github.com/mikegough/CDT-CEQA-California-GIS/blob/master/Calculate_CEQA_Requirements_and_Exemptions_Statewide.py"
                    target="_blank"
                    rel="noreferrer"
                >
                    here
                </a>
                .
            </p>

            <h4 className="title is-4">Datasets</h4>
            <p>
                All publicly available data that are displayed as layers in the tool are available for download from the
                customized gallery of datasets from Data basin as File Geodatabase Feature Classes & Rasters, as well as
                Shapefiles. A Data Basin log in is required to view and download the data which can be found at{' '}
                <a
                    href="https://databasin.org/galleries/b1d534e326364bdcb98fdd437870e7a1"
                    target="_blank"
                    rel="noreferrer"
                >
                    Site check tool - data gallery
                </a>
                . Metadata for each dataset is accessible through Data Basin. Please note that the level of detail in
                the metadata is dependent entirely on the data providers. Depending on budget availability, data will be
                updated on a yearly basis every fall before a new version of the tool is released in January. We thank
                California’s Metropolitan Planning Organizations for providing existing High Quality transit stops,
                corridors, Vehicle Miles Traveled data, and specific plan data where available. We thank CALTRANS for
                providing data on existing major transit stops and for the statewide California State Transportation
                Demand Model (CSTDM) dataset.
            </p>

            <h4 className="title is-4">Summary table of Input data used in the spatial analysis</h4>
            <div className="table-wrapper">
                <table className="table">
                    <thead>
                        <tr>
                            <th>Requirements</th>
                            <th>ID#</th>
                            <th>Input Data</th>
                            <th>Approach</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <th colSpan={4}>Location</th>
                        </tr>

                        <tr>
                            <td>Parcel data</td>
                            <td>n/a</td>
                            <td>
                                Provided by LCI and current as of February 2023.
                                <br />
                                Not available on Data Basin as its size is beyond the maximum limit
                            </td>
                            <td>
                                Several processing steps were performed on the statewide dataset to create the county
                                level datasets in this geodatabase.
                            </td>
                        </tr>

                        <tr>
                            <td>Urbanized Area PRC 21071</td>
                            <td>2.1</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/9d93fcf4045245c6981979c24235c792/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Urbanized Area PRC 21071 v1.0 (CAL FIRE 2021 & 5YR ACS 2020)
                                </a>
                            </td>
                            <td>Select parcels meeting the requirements in PRC 21071</td>
                        </tr>
                        <tr>
                            <td>Urban Area PRC 21094.5</td>
                            <td>2.2</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/270c4d215bfd4dc786fb8e90df1fa26b/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Urban Area PRC 21094.5 v1.0
                                </a>
                            </td>
                            <td>Select parcels meeting the requirements in PRC 21094.5</td>
                        </tr>
                        <tr>
                            <td>Within City Limits</td>
                            <td>2.3</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/d02ce644f9484d32bf9b9d98497838a1/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    California Incorporated Cities
                                </a>
                            </td>
                            <td>Select parcels falling with city limits.</td>
                        </tr>
                        <tr>
                            <td>Unincorporated areas</td>
                            <td>2.4</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/7b615f16a8a747e1b20fc6c869a7deb7"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Board of Equalization Incorporated City Boundaries
                                </a>
                            </td>
                            <td>Select parcels falling within unincorporated areas.</td>
                        </tr>
                        <tr>
                            <td>Within a Metropolitan Planning Organization Boundary</td>
                            <td>2.5</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/41d649c31c594f57a6f6aae0b40db37b"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Metropolitan Planning Organization (MPO), California
                                </a>
                            </td>
                            <td>Select parcels that fall within an MPO.</td>
                        </tr>
                        <tr>
                            <td>Covered by specific Plan (EIR certified)</td>
                            <td>2.6</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/0c91ba6558ad4cbf9cd2ce1ee5e4752d/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Specific Plan Boundaries (as of October 2023)
                                </a>
                            </td>
                            <td>Select parcels that intersect with the Specific plan boundaries.</td>
                        </tr>
                        <tr>
                            <td>Urbanized area or urban cluster</td>
                            <td>2.7</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/319d80e57dc7494495570a890cbaafc8/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    2020 Urban Areas (US Census)
                                </a>
                            </td>
                            <td>Select parcels that either fall within an urban area or urban cluster.</td>
                        </tr>
                        <tr>
                            <th colSpan={4}>Transit Proximity Requirements</th>
                        </tr>
                        <tr>
                            <td>Within ½ mile of Existing Major Transit Stops</td>
                            <td>3.1</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/641fd566ce784f1db1e7df6aef143043/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Within ½ mile of Existing Major Transit Stops (as of December 2023)
                                </a>
                            </td>
                            <td>Select parcels that fall within ½ mile of Major Transit Stops</td>
                        </tr>
                        <tr>
                            <td>Within ¼ mile of Existing High Quality Transit Corridors</td>
                            <td>3.2</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/18c44b3c85ed4c1395699067e3d2ee23/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Within ¼ mile of Existing High-Quality Transit Corridors (as of December 2023)
                                </a>
                            </td>
                            <td>Select parcels within ¼ mile of High Quality Transit Corridor</td>
                        </tr>
                        <tr>
                            <td>Transit Priority Area PRC 21099</td>
                            <td>3.3</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/c27b70a214c84b0d8d79583f2b699c99/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Transit Priority Areas (as of December 2023)
                                </a>
                            </td>
                            <td>Select parcels intersecting Transit Priority Areas defined by PRC 21099</td>
                        </tr>
                        <tr>
                            <td>Within ½ mile of Existing High Quality Transit Corridors</td>
                            <td>3.4</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/b60ac404f2d74586932b17d891bfe2a4/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Within ½ mile of Existing High-Quality Transit Corridor (as of December 2023)
                                </a>
                            </td>
                            <td>Select parcels intersecting ½ mile of High Quality Transit Corridors</td>
                        </tr>
                        <tr>
                            <td>Within ½ mile of Stops along Existing High Quality Transit Corridors</td>
                            <td>3.5</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/f991dab934634522a0ffe2fe2d75fc59/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Within ½ mile of Stops Along High-Quality Transit Corridors (as of December 2023)
                                </a>
                            </td>
                            <td>
                                Select parcels intersecting ½ mile of Stops along existing High Quality Transit
                                Corridors
                            </td>
                        </tr>
                        <tr>
                            <td>Low Vehicle Travel Area - 15% below regional average</td>
                            <td>3.6</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/4e189c878e094cbfb0a000ae8ad04948/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    15 % Below Regional Average Vehicle Miles Traveled (VMT) Per Capita
                                </a>
                            </td>
                            <td>
                                Selected parcels intersecting with per capita residential VMT reporting units 15% below
                                regional average
                            </td>
                        </tr>
                        <tr>
                            <td>Low Vehicle Travel Area - below regional average</td>
                            <td>3.8</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/d155d4bec8774c7db030ab090bd86359/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Below Regional Average Vehicle Miles Traveled (VMT) Per Capita
                                </a>
                            </td>
                            <td>
                                Selected parcels intersecting with per capita residential VMT reporting units below
                                regional average
                            </td>
                        </tr>
                        <tr>
                            <th colSpan={4}>Environmental Constraints</th>
                        </tr>
                        <tr>
                            <td>Wetlands</td>
                            <td>8.1</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/bf488e8504a24510b7ce88d1567b4ba6/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    California Aquatic Resource Inventory (CARI) Wetlands
                                </a>
                            </td>
                            <td>Avoid parcels that intersect with wetland polygons</td>
                        </tr>
                        <tr>
                            <td>Riparian Areas</td>
                            <td>8.2</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/34e7670e42e74206807d67eb5062ac57/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Natural Communities Commonly Associated with Groundwater; Vegetation6
                                </a>
                            </td>
                            <td>Avoid parcels that intersect with riparian polygons</td>
                        </tr>
                        <tr>
                            <td>Habitats</td>
                            <td>8.3</td>
                            <td>
                                <div>
                                    <a
                                        href="https://databasin.org/datasets/1cfc5d39fdc84647b7ce55ff92426bd3/"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Special Habitats of California (CDFW, Cal Fire 2015)
                                    </a>
                                </div>
                            </td>
                            <td>Avoid parcels that intersect with special habitat polygons</td>
                        </tr>
                        <tr>
                            <td>Prime farmlands or farmlands of statewide importance</td>
                            <td>8.6</td>
                            <td>
                                <div>
                                    <a
                                        href="https://databasin.org/datasets/24b1bd517d1e461b9dea7fc72e3dda3d/"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        California Important Farmland (as of 2020)
                                    </a>
                                </div>
                            </td>
                            <td>Avoid parcels that intersect with farmlands coded as either P or S.</td>
                        </tr>
                        <tr>
                            <td>Earthquake/Seismic Fault Zones</td>
                            <td>9.2</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/d4e8e99004f2430ca80d05ee261b2103/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    CGS Alquist-Priolo Fault Zones{' '}
                                </a>
                            </td>
                            <td>Avoid parcels that intersect with fault zones</td>
                        </tr>
                        <tr>
                            <td>Wildfire Hazard</td>
                            <td>9.3</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/fbb8a20def844e168aeb7beb1a7e74bc/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    CAL FIRE FRAP Fire Hazard Severity Zones
                                </a>
                            </td>
                            <td>
                                Avoid parcels that intersect with wildfire hazard levels that are high, very high and
                                extreme.
                            </td>
                        </tr>
                        <tr>
                            <td>Flood Plain</td>
                            <td>9.4</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/845bd265f7604fd499da8620b5d6009f"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    National Flood Hazard Layer (NFHL), California
                                </a>
                            </td>
                            <td>Avoid parcels that intersect with FEMA zones A, A99, AE, AH, AO, and VE.</td>
                        </tr>
                        <tr>
                            <td>Landslide Hazard</td>
                            <td>9.5</td>
                            <td>
                                <div>
                                    <a
                                        href="https://databasin.org/datasets/8caa4e91ea5642c6bdc72308e6e4dc4f/"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Landslide Hazard
                                    </a>
                                </div>
                            </td>
                            <td>
                                Avoid parcels where &gt; 20% of the parcel area overlaps with a landslide hazard zone.
                            </td>
                        </tr>
                        <tr>
                            <td>State Conservancy Boundary</td>
                            <td>9.6</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/9d0ab79e126647ac821a28dd0c456f77"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    State of California Conservancies
                                </a>
                            </td>
                            <td>Avoid parcels that intersect with state conservancy boundaries</td>
                        </tr>
                        <tr>
                            <td>Protected areas</td>
                            <td>9.8</td>
                            <td>
                                <a
                                    href="https://databasin.org/datasets/a287d2ae2df341b7b54b7a324c67a734"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Protected Area Mask for Development Exclusion
                                </a>
                            </td>
                            <td>Was used as a mask to exclude protected areas from the analysis.</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <h4 className="title is-4">GIS Methodology</h4>
            <p>
                The following describes the methods used for calculating the individual requirements used in assessing
                whether or not a parcel qualifies for an Exemption or Streamlining Provision. Calculations were
                performed using the ArcPy site package for ArcGIS 10.6 (see full script{' '}
                <a
                    href="https://github.com/mikegough/CDT-CEQA-California-GIS/blob/master/Calculate_CEQA_Requirements_and_Exemptions_Statewide.py"
                    target="_blank"
                    rel="noreferrer"
                >
                    here
                </a>
                ).
            </p>

            <h4 className="title is-4">Parcel dataset</h4>
            <p>
                Several processing steps were performed on the statewide dataset to create the county level datasets in
                this geodatabase. The bulk parcel data files are current as of February 2023 and were received from
                ParcelQuest through LCI. These are summarized below and in the lineage statement:
            </p>
            <ol className="is-indented">
                <li>
                    Project from WGS_1984_Web_Mercator_Auxiliary_Sphere to NAD_1983_California_Teale_Albers (datum
                    transformation = "WGS_1984_(ITRF00)_To_NAD_1983").
                </li>

                <li>Remove duplicate parcels (delete parcels having an identical SHAPE).</li>
                <li>
                    Add, and calculate, missing fields (a unique ID, State Name, Latitude, Longitude). Note that the
                    unique ID field (cbi_parcel_id_fips_apn_oid) is based on the fips_apn and the OBJECTID).
                </li>

                <li>
                    Determine the zip code for each parcel (spatial join with zip code boundaries using the
                    HAVE_THEIR_CENTER_IN method). Zip code boundaries were acquired from the California State Geoportal:
                    https://gis.data.ca.gov/datasets/CDEGIS::california-zip-codes/about
                </li>

                <li>
                    Determine the MPO (Metropolitan Planning Organization) that each parcels falls within (spatial join
                    with MPO boundaries using the HAVE_THEIR_CENTER_IN method). MPO Boundaries were produced by the U.S.
                    Census Bureau and acquired from Data Basin on 11/25/2019:
                    https://databasin.org/datasets/41d649c31c594f57a6f6aae0b40db37b/
                </li>

                <li>
                    Determine the Specific Plan that each parcels falls within (spatial join with Specific Plan
                    boundaries using the HAVE_THEIR_CENTER_IN method). Specific Plan boundaries were provided by Justin
                    Heyerdahl on 5/22/2023 (used in run #4).
                </li>
                <li>Separate into individual county datasets (SELECT).</li>
            </ol>

            <h4 className="title is-4">Urbanized Area PRC 21071 [2.1]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <ol className="is-indented">
                <li>2023 Incorporated cities from CAL FIRE.</li>
                <li>TIGER CENSUS Current Place, State-based (2021) dataset.</li>
                <li>
                    2020 US CENSUS 5 Year American Community Survey (ACS) database for the state of California. Note:
                    The Unincorporated Islands used in this analysis were created by identifying empty areas in the CAL
                    FIRE incorporated cities dataset that were completely surrounded by one or more incorporated cities
                    (i.e., donut holes). The population estimate values assigned to the unincorporated island polygons
                    were generated by aggregating block level data from the 2010 CENSUS.
                </li>
            </ol>
            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>Copy US CENSUS 5YR ACS Population Estimates from a CSV to a GDB Table</li>
                <li>Project TIGER CENSUS Current Place</li>
                <li>Join ACS Population Estimates to TIGER CENSUS Current Place based on GEOID</li>
                <li>Project CAL FIRE Incorporated Cities</li>
                <li>
                    Remove 'Arcata (Jacoby Crk Forst)' from CAL FIRE Incorporated Cities. No population data for this
                    polygon.
                </li>
                <li>
                    <p>
                        The CAL FIRE Incorporated Cities dataset does not have a GEOID, so the city name must be used to
                        join the TIGER CENSUS Current Place/ACS population data to it. Make edits to the CAL FIRE City
                        name in order to match the city name in the TIGER CENSUS Current Place dataset in order for the
                        join to work. The edits made are as follows (From: To):
                    </p>
                    <p>
                        'Ventura': 'San Buenaventura (Ventura)'
                        <br />
                        'Paso Robles': 'El Paso de Robles (Paso Robles)'
                        <br />
                        'La Canada Flintridge': 'La Cañada Flintridge'
                        <br />
                        'Angels Camp':'Angels'
                    </p>
                </li>
                <li>
                    Join the TIGER CENSUS Current Place with ACS Population Estimates to the CAL FIRE Incorporated
                    Cities dataset using the modified city name field.
                </li>
                <li>Perform a Self Union to fill gaps.</li>
                <li>Select gaps to identify Unincorporated Islands</li>
                <li>Project Census Blocks 2010 (with Population)</li>
                <li>
                    Intersect Census Blocks 2010 (with Population) with Unincorporated Islands to Create Unincorporated
                    Islands with 2010 Population
                </li>
                <li>
                    Dissolve Unincorporated Islands with 2010 Population and take the sum of the block level population
                    to Unincorporated Islands with 2010 Population SUM
                </li>
                <li>Union the incorporated cities and the unincorporated islands</li>
                <li>Add necessary fields to store intermediate variables.</li>
            </ol>

            <p>
                <strong>
                    Create a python script that uses an update cursor to iterate over each polygon in the unioned
                    dataset and perform the following steps:
                </strong>
            </p>
            <p>
                <strong>Calculate the following variables:</strong>
            </p>

            <ol className="is-indented">
                <li>The number of surrounding cities</li>
                <li>The total area of the surrounding cities</li>
                <li>The total population of the surrounding cities</li>
                <li>A list of the populations of the surrounding cities</li>
                <li>
                    If there are one or more surrounding cities, calculate the population density of the surrounding
                    cities.
                </li>
            </ol>

            <p>
                <strong>If a polygon is an incorporated city:</strong>
            </p>

            <ol className="is-indented">
                <li>If the city has a population &gt; 100,000 (urbanized_area_prc_21071 = TRUE)</li>
                <li>
                    If the city has a population &lt; 100,000:
                    <ol className="is-indented" type="a">
                        <li>Get the sum of the top two contiguous city populations.</li>
                        <li>
                            If the city plus two contiguous incorporated cities &gt;= 100,000 (urbanized_area_prc_21071
                            = TRUE), otherwise (urbanized_area_prc_21071 = FALSE)
                        </li>
                    </ol>
                </li>
            </ol>

            <p>
                <strong>If the polygon is an unincorporated island:</strong>
            </p>

            <ol className="is-indented">
                <li>
                    Add the population estimate of the unincorporated island to the population estimate of the
                    surrounding cities.
                </li>
                <li>
                    If the value calculated above is &gt;= 100,000 and the population density of the unincorporated
                    island is &gt;= the population density of the surrounding cities (urbanized_area_prc_21071 = TRUE),
                    otherwise (urbanized_area_prc_21071 = FALSE).
                </li>
            </ol>

            <h4 className="title is-4">Urbanized Area PRC 21094.5 [2.2]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <ol className="is-indented">
                <li>2023 Incorporated cities from CAL FIRE.</li>
                <li>TIGER CENSUS Current Place, State-based (2021) dataset.</li>
                <li>2020 US CENSUS 5 Year American Community Survey (ACS) database for the state of California.</li>
            </ol>
            <p>
                <u>Methods:</u>
            </p>
            <p>
                This dataset was derived from the previous calculation made for Urbanized Area PRC 21071 [2.1] since the
                only difference between the criteria for that requirement and the criteria for Urban Area PRC 21094.5 is
                that any incorporated city meets the criteria for Urban Area PRC 21094.5, whereas an incorporated city
                had to meet certain population criteria for Urbanized Area PRC 21071. Consequently, the values in the
                urban_area_prc_21094_5 field were calculated using the following expression: "community_type =
                'Incorporated City' or urbanized_area_prc_21071 = 1" In other words, if a polygon is an incorporated
                city, or it met the criteria for Urbanized Area PRC 21071 , then it meets the criteria for Urban Area
                PRC 21094.5
            </p>

            <h4 className="title is-4">Within City Limits [2.3]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>California Incorporated Cities, 2023 (CAL FIRE)</p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>Select City = "Cities" from dataset</li>
                <li>Select parcels that "HAVE THEIR CENTER IN" city boundaries</li>
                <li>Selected parcels populated with "1"</li>
                <li>Unselected parcels populated with "0"</li>
            </ol>

            <h4 className="title is-4">Within Unincorporated Areas [2.4]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>California Incorporated Cities, 2023 (CAL FIRE)</p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>Project California Incorporated Cities 2021 boundaries.</li>
                <li>Select City = "Unincorporated" from the input dataset</li>
                <li>Select parcels that "HAVE_THEIR_CENTER_IN" Unincorporated areas</li>
                <li>Selected parcels populated with "1"</li>
                <li>Unselected parcels populated with "0"</li>
            </ol>

            <h4 className="title is-4">Within MPOs [2.5]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>Metropolitan Planning Organization (MPO) Boundaries</p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>Select parcels that "HAVE THEIR CENTER IN" Dissolved MPO Boundaries</li>
                <li>Selected parcels populated with "1"</li>
                <li>Unselected parcels populated with "0"</li>
            </ol>

            <h4 className="title is-4">Covered by a Specific Plan [2.6]</h4>
            <p>Outreach Summary</p>
            <p>
                Outreach to agencies across California jurisdictions (cities/towns, counties) occurred from September 12
                through October 10, 2023, inviting contacts to participate in an online survey and submit local datasets
                to help update the specific plan boundaries dataset used in Site Check.
            </p>
            <p>Data Aggregation & Attribute Updates</p>
            <p>
                Data submissions through the specific plan online survey were collected in DropBox and downloaded for
                processing on October 11, 2023. Submitted geospatial data were reprojected into a common spatial
                reference system: NAD 1983 California (Teale) Albers (Meters) [EPSG 3310] as feature classes within a
                file geodatabase and renamed using a common format of JurisdictionName_SourceName. In cases of multiple
                submissions from the same jurisdiction resulting in multiple datasets, the operative dataset was
                identified using information provided in the comment field from the survey responses or clarification
                received via direct email. All duplicate datasets were visually assessed for similarity/dissimilarity in
                geographic coverage as well as the content and number of rows and columns within the attribute table.
            </p>
            <p>
                Eight standardized attribute fields were added to all reprojected and renamed feature classes and then
                individually populated with information from the source data (e.g., Specific Plan Name). If source data
                already contained one or more of the eight new attribute fields then the source field was used as the
                default. Datasets were then merged into a compiled, statewide dataset retaining only the standardized
                attributes. On a case by case basis, jurisdiction names were populated using information received
                through the specific plan survey, and to a lesser extent date of adoption and online link.
            </p>
            <p>
                Due to the variation in how a specific plan boundary is defined and digitized by each jurisdiction
                (parcel-level vs generalized boundary) as well as how data is managed by each jurisdiction, multiple
                individual parcels or areas assigned to the same specific plan were dissolved into a multi-part feature
                where possible. Underlying issues in shape geometry errors within some source data necessitated those
                particular fragmented specific plan boundaries not be dissolved and were instead included as provided by
                the source.
            </p>
            <p>Data Completion and Variation</p>
            <p>
                Data completion varied widely across jurisdictions, with a minority providing data sufficient to
                populate all six key attribute fields. In several cases, specific plans were not provided as stand-alone
                datasets but within jurisdiction-wide zoning/parcel datasets, and additional querying was required to
                extract the relevant boundaries. Jurisdictions also varied on their interpretation of a specific plan
                boundary, with some providing parcel-level boundaries and others providing a generalized boundary that
                frames the outer border of all applicable parcels within. Specific plan boundaries were ingested into
                the compiled dataset without modification, except to project them into a common coordinate system for
                standardized mapping. In a minority of cases, polygon geometry had to be corrected before compiling into
                the final dataset.
            </p>
            <p>Addressing Data Gaps and Remaining Limitations</p>
            <p>
                There were several instances of datasets received with no attribute information at all or missing plan
                names that would allow boundaries to be distinguished from one another. In these cases, spatial
                boundaries were cross-referenced with maps in plan documents (if links were provided via the survey) to
                populate a plan name at the minimum. Therefore, some interpretation was made to populate this field in
                the compiled dataset with source dataset fields likely to be the closest match. In these cases, date of
                adoption and online link were also populated if these were readily provided.
            </p>
            <p>
                For local jurisdictions that did not submit data, data was instead sourced from the relevant Municipal
                Planning Organization (MPO) acquired in the prior outreach effort from May 2023. While this compiled
                dataset aims to provide as complete of coverage across the State as possible, this coverage is entirely
                dependent on local and regional sources providing access to accurate, timely datasets of sufficient
                quality and completion for inclusion. Therefore, some gaps may still remain in areas where currently no
                specific plan boundaries have been made available.
            </p>
            <p>
                <u>Inputs:</u>
            </p>
            <p>Specific Plan Boundaries (Local Jurisdictions, October 2023)</p>
            <p>Specific Plan Boundaries (Municipal Planning Organizations [MPO], May 2023)</p>
            <p>California City and County Boundaries (CalFire 2023, 2021)</p>
            <p>
                <u>Processing October 2023 Local Jurisdiction Data:</u>
            </p>

            <ol className="is-indented">
                <li>
                    Project October 2023 local jurisdiction data into NAD 1983 California (Teale) Albers (Meters) [EPSG
                    3310].
                </li>
                <li>
                    Create empty geodatabase feature class with standardized fields:
                    <ul>
                        <li>J_Type (Jurisdiction Type)</li>
                        <li>J_Name (Jurisdiction Name)</li>
                        <li>SP_Name (Specific Plan Name)</li>
                        <li>SP_Date (Date of Adoption or Last Update)</li>
                        <li>GIS_Src (GIS Source)</li>
                        <li>Src_Date (GIS Source Date)</li>
                        <li>Src_Affil (Source Affiliation)</li>
                        <li>QUERY (Outreach Effort Date)</li>
                    </ul>
                </li>
                <li>Load data to empty feature class using field re-mapping.</li>
            </ol>

            <p>
                <u>Integrating May 2023 MPO Data to Fill Coverage Gaps:</u>
            </p>

            <ol className="is-indented">
                <li>
                    Use California City and County Boundaries to assign jurisdiction names to May 2023 specific plans
                    using identical naming convention as the October 2023 dataset.
                </li>
                <li>
                    Exclude May 2023 specific plans from any city/town or county jurisdiction that already has specific
                    plan coverage within the October 2023 local jurisdiction dataset.
                </li>
                <li>
                    Exclude May 2023 specific plan names containing text "Precise" or "Future" to filter out precise
                    plans and future specific plans that do not qualify as active specific plans.
                </li>
                <li>Exclude May 2023 specific plan names listed as "Unknown" with no other context.</li>
                <li>
                    Exclude May 2023 specific plans that contain the inside center point of any specific plan from the
                    October 2023 local jurisdiction dataset.
                </li>
                <li>
                    Exclude May 2023 specific plans that have a large boundary overlap with specific plans from the
                    October 2023 local jurisdiction dataset.
                </li>
                <li>For each step above, visually assess results and modify screened selection as needed.</li>
                <li>Append retained May 2023 data to October 2023 feature class using field re-mapping.</li>
            </ol>

            <p>
                <u>Assigning Parcel Eligibility under Requirement 2.6:</u>
            </p>

            <ol className="is-indented">
                <li>Parcels that "HAVE_THEIR_CENTER_IN" Specific Plan polygons are selected.</li>
                <li>Intersecting parcels populated with a value of 1.</li>
                <li>Non-intersecting parcels populated with a value of 0.</li>
            </ol>

            <h4 className="title is-4">Urbanized Area or Urban Cluster [2.7]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>Urban Areas (US Census)</p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>Select parcels that "HAVE_THEIR_CENTER_IN" urban areas</li>
                <li>Selected parcels populated with "1"</li>
                <li>Unselected parcels populated with "0"</li>
            </ol>

            <h4 className="title is-4">Within ½ mile of existing Major Transit Stop [3.1]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>California High-Quality (HQ) Transit Stops (CalTrans)</p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>
                    Subset HQ Transit Stops to include hqta_type = major_stop_bus, major_stop, major_stop_rail,
                    major_stop_brt, and major_stop_ferry.
                </li>
                <li>Buffer subset stops by ½-mile.</li>
                <li>Parcels selected if intersecting the ½ mile buffer.</li>
                <li>Intersecting parcels populated with a value of 1.</li>
                <li>Non-intersecting parcels populated with a value of 0.</li>
            </ol>

            <p>
                <u>Source:</u>
            </p>
            <p>CA HQ Transit Stops (CalTrans)</p>
            <p>
                <a href="https://gisdata-caltrans.opendata.arcgis.com/datasets/f6c30480f0e84be699383192c099a6a4_0/about">
                    https://gisdata-caltrans.opendata.arcgis.com/datasets/f6c30480f0e84be699383192c099a6a4_0/about
                </a>
            </p>

            <h4 className="title is-4">Within ¼ mile of Existing High Quality Transit Corridor [3.2]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>California High-Quality (HQ) Transit Areas (CalTrans)</p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>Subset HQ Transit Areas to hqta_type = hq_corridor_bus.</li>
                <li>Apply a negative ¼ mile buffer to the subset HQ Transit Areas.</li>
                <li>Parcels selected if intersecting the remaining ¼ mile buffer.</li>
                <li>Intersecting parcels populated with a value of 1.</li>
                <li>Non-intersecting parcels populated with a value of 0.</li>
            </ol>

            <p>
                <u>Source:</u>
            </p>
            <p>CA HQ Transit Areas (CalTrans)</p>

            <p>
                <a href="https://gisdata-caltrans.opendata.arcgis.com/datasets/863e61eacbf3463ab239beb3cee4a2c3_0/about">
                    https://gisdata-caltrans.opendata.arcgis.com/datasets/863e61eacbf3463ab239beb3cee4a2c3_0/about
                </a>
            </p>

            <h4 className="title is-4">Transit Priority Area PRC 21099 [3.3]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>California High-Quality (HQ) Transit Stops (CalTrans 2023)</p>
            <p>Planned Major Transit Stops within 20+ years (RTP/MTP 2020)</p>
            <p>Planned Major Transit Stops within 5 years (RTIP/MTIP 2020)</p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>Project all inputs into NAD 1983 California (Teale) Albers (Meters) [EPSG 3310].</li>
                <li>
                    Subset the 2023 HQ Transit Stops to include hqta_type = major_stop_bus, major_stop, major_stop_rail,
                    major_stop_brt, and major_stop_ferry and export as a new feature containing existing major transit
                    stops.
                </li>
                <li>Merge existing major transit stops with planned major transit stops.</li>
                <li>Buffer the merged dataset by ½ mile.</li>
                <li>Select parcels that intersect the ½ mile buffer.</li>
                <li>Intersecting parcels populated with a value of 1.</li>
                <li>Non-intersecting parcels populated with a value of 0.</li>
            </ol>

            <p>
                <u>Source Links:</u>
            </p>
            <p>Existing and Planned Major Transit Stops (CalTrans 2023, MPOs 2020)</p>

            <p>
                <a href="https://databasin.org/datasets/a1117babc64c479ca72b0cf1f86ce268/">
                    https://databasin.org/datasets/a1117babc64c479ca72b0cf1f86ce268/
                </a>
            </p>

            <h4 className="title is-4">Within ½ mile of Existing High Quality Transit Corridor [3.4]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>California High-Quality (HQ) Transit Areas (CalTrans)</p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>Subset HQ Transit Areas to hqta_type = hq_corridor_bus</li>
                <li>Parcels selected if intersecting the subset HQ Transit Areas</li>
                <li>Intersecting parcels populated with a value of 1.</li>
                <li>Non-intersecting parcels populated with a value of 0.</li>
            </ol>

            <p>
                <u>Source:</u>
            </p>
            <p>CA HQ Transit Areas (CalTrans)</p>

            <p>
                <a href="https://gisdata-caltrans.opendata.arcgis.com/datasets/863e61eacbf3463ab239beb3cee4a2c3_0/about">
                    https://gisdata-caltrans.opendata.arcgis.com/datasets/863e61eacbf3463ab239beb3cee4a2c3_0/about
                </a>
            </p>

            <h4 className="title is-4">Within ½ mile of a Stop Along a High Quality Transit Corridor [3.5]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>California High-Quality (HQ) Transit Areas (CalTrans)</p>
            <p>California HQ Transit Stops (CalTrans)</p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>Subset HQ Transit Areas to hqta_type = hq_corridor_bus.</li>
                <li>Select all HQ Transit Stops located within subset HQ Transit Areas.</li>
                <li>Buffer selected HQ Transit Stops within HQ corridors by ½ mile.</li>
                <li>Parcels selected in intersecting the ½ mile buffer.</li>
                <li>Intersecting parcels populated with a value of 1.</li>
                <li>Non-intersecting parcels populated with a value of 0.</li>
            </ol>

            <p>
                <u>Source:</u>
            </p>
            <p>CA HQ Transit Stops (CalTrans)</p>

            <p>
                <a href="https://gisdata-caltrans.opendata.arcgis.com/datasets/f6c30480f0e84be699383192c099a6a4_0/about">
                    https://gisdata-caltrans.opendata.arcgis.com/datasets/f6c30480f0e84be699383192c099a6a4_0/about
                </a>
            </p>
            <p>CA HQ Transit Areas (CalTrans)</p>

            <p>
                <a href="https://gisdata-caltrans.opendata.arcgis.com/datasets/863e61eacbf3463ab239beb3cee4a2c3_0/about">
                    https://gisdata-caltrans.opendata.arcgis.com/datasets/863e61eacbf3463ab239beb3cee4a2c3_0/about
                </a>
            </p>

            <h4 className="title is-4">Low Vehicle Travel Area - 15% Below Regional Average [3.6]</h4>

            <p>
                <u>Inputs:</u>
            </p>
            <p>California Department of Transportation (CalTrans) 2020 Statewide Travel Demand Model Data</p>
            <p>Municipal Planning Organization (MPO) Regional Travel Demand Model Data</p>
            <ul className="is-indented">
                <li>
                    Butte County Association of Governments (BCAG), Fresno County Council of Governments (FCOG), Madera
                    County Transportation Commission (MCTC), Metropolitan Transportation Commission (MTC), Sacramento
                    Area Council of Governments (SACOG), San Diego Association of Governments (SANDAG), Southern
                    California Association of Governments (SCAG)
                </li>
            </ul>

            <p>MPO Data Outreach and Acquisition</p>

            <p>
                In April, 2023, CBI conducted outreach to multiple Metropolitan Planning Organizations (MPO) on behalf
                of the California Office of Land Use and Climate Innovation (LCI) to solicit for and acquire updated residential
                vehicle miles traveled (VMT) datasets based on their respective regional travel demand models. Requested
                geospatial data (as shapefile or feature class) included metrics for VMT per capita (by traffic analysis
                zone [TAZ]), VMT per capita (for region), date layer was created, and model output year. Based on that
                outreach, data of varying completeness was received from BCAG, FCOG, SACOG, SANDAG, SCAG, and SLOCOG.
                After quality control/quality assurance (QAQC) review, only datasets from BCAG, FCOG, and SACOG were
                included in this Version 2.1 update (see note below on data exclusions for more detail).
            </p>

            <p>MPO Data Aggregation & Attribute Updates</p>

            <p>
                Data that passed a QAQC check was aggregated from individual MPO's regional transit demand model output
                datasets and used to provide residential VMT per capita estimates throughout the area of each respective
                MPO's jurisdictional boundary. This data was projected into NAD 83 California Teale Albers (EPSG: 3310)
                and appended to the existing Version 2.0 VMT per capita dataset. A query field (QUERY) was added to
                denote new data acquired for Version 2.1 (2023 - New Data), existing data to be included in Version 2.1
                (2023 - Prior Data), and existing data to be excluded from Version 2.1 (2023 - Exclude). The exclusion
                category was used to set a definition query to filter out older data that was not used in Version 2.1
                (i.e., BCAG, FCOG, SACOG, CSTDM 2016). A date field (SiteCheck) was added to track the year in which the
                data was added and used to update Site Check (e.g., 2023).
            </p>

            <p>Updates to California Statewide Travel Demand Model (CSTDM) Data</p>

            <p>
                Versions 1.0 and 2.0 of the VMT per capita dataset used output data from the California Department of
                Transportation's (CalTrans) 2016 CSTDM to fill in all areas without MPO-derived VMT per capita coverage.
                Based on subsequent conversations with LCI (May 2023), Version 2.0 of the VMT per capita dataset
                replaced use of the 2016 CSTDM in favor of the more recent 2020 CSTDM and adopted a revised ruleset for
                aggregating MPO regional travel model data with the CSTDM.
            </p>

            <p>
                In Version 2.1, MPO datasets (when available) were the authoritative source for VMT per capita estimates
                within the entire jurisdictional boundary of each respective MPO, and therefore all data across an MPO
                comes from a single source: their regional travel demand model. For areas with no MPO and no regional
                travel demand model, VMT per capita estimates were instead sourced from CalTran's 2020 CSTDM. Within the
                2020 CSTDM, regional VMT per capita (regional average) is calculated in either of two ways. For areas
                within an MPO, the regional average is based on the total VMT and total population across the MPO's
                jurisdictional boundary. For areas outside of an MPO, the regional average is based on the total VMT and
                total population across the county. These values were calculated by CalTrans and left unaltered for
                inclusion in Version 2.1 of the VMT per capita dataset.
            </p>

            <p>Calculation of 15% Below Regional Average VMT per Capita</p>

            <p>
                VMT is a model output (metric) that describes travel behavior for every resident as the number of miles
                traveled on a typical weekday when school is in session (CalTrans). Total VMT (sum) and total
                residential population (sum) are calculated by a regional or statewide travel demand model and
                summarized by Traffic Analysis Zone (TAZ), which are discrete geographic units used for both modeling
                and reporting. Dividing total VMT by total population within each TAZ produces the metric of residential
                VMT per capita. Similarly, dividing total VMT by total population across an entire region produces the
                metric of regional VMT per capita (i.e., regional average). Dividing the residential VMT per capita
                within each TAZ by the regional average and multiplying by 100 produces the metric called percent VMT. A
                residential VMT per capita that equals the regional average has a percent VMT of 100%. Areas with a
                residential VMT per capita that can be considered less than or equal to 15% below average have a percent
                VMT ranging from 85% to 99%. Within the VMT per capita dataset, all areas that fall within this range
                were denoted as such in the VMT Category field as 15% Below Average, and then extracted as a separate
                feature class representing this VMT category solely.
            </p>

            <p>Treatment of No Data and Anomalous Data</p>

            <p>
                Areas where regional or statewide models could not produce reliable estimates and reported as No Data
                are left unaltered. MPO data based on regional models that reported numerous residential VMT per capita
                estimates higher than 100 were flagged as anomalous, and data for the region was instead sourced from
                the 2020 CSTDM.
            </p>

            <p>Special Note on Excluded Data</p>

            <p>
                New VMT datasets were initially received from San Diego Association of Governments (SANDAG), Southern
                California Association of Governments (SCAG), and San Luis Obispo Council of Governments (SLOCOG). Per
                California Office of Land Use and Climate Innovation (LCI), these datasets did not pass quality QAQC inspection
                based on unexplained missing data as well as anomalous data for VMT per capita values greater than 100,
                and as such were excluded from the Version 2.1 update. Existing MPO data from Version 1.0 and Version
                2.0 was instead used for SANDAG and SCAG, while SLOCOG was replaced with CSTDM (2020) data.
            </p>

            <p>Full List of MPO Outreach (April 2023)</p>

            <p>
                Association of Monterey Governments (AMBAG), Butte County Association of Governments (BCAG), Kings
                County Association of Governments (KCAG), Fresno Council of Governments (FCOG), Kern Council of
                Governments (KCOG), Merced Association of Governments (MCAG), Madera County Transportation Commission
                (MCTC), Metropolitan Transportation Commission (MTC), Sacramento Area Council of Governments (SACOG),
                San Diego Association of Governments (SANDAG), San Luis Obispo Council of Governments (SLOCOG), Santa
                Barbara County Association of Governments (SBCAG), Shasta Regional Transportation Agency (SRTA),
                Southern California Association of Governments (SCAG), Stanislaus Council of Governments (SCOG), Tulare
                County Association of Governments (TCOG), Tahoe Regional Planning Agency (TRPA)
            </p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>Parcels selected by intersecting with VMT polygons</li>
                <li>Intersecting parcels populated with "0"</li>
                <li>Non-intersecting parcels populated with "1"</li>
            </ol>

            <h4 className="title is-4">Low Vehicle Travel Area - Below Regional Average [3.8]</h4>

            <p>
                <u>Inputs:</u>
            </p>
            <p>California Department of Transportation (CalTrans) 2020 Statewide Transit Demand Model Data</p>

            <p>Municipal Planning Organization (MPO) Regional Transit Demand Model Data</p>

            <ul className="is-indented">
                <li>
                    Butte County Association of Governments (BCAG), Fresno County Council of Governments (FCOG), Madera
                    County Transportation Commission (MCTC), Metropolitan Transportation Commission (MTC), Sacramento
                    Area Council of Governments (SACOG), San Diego Association of Governments (SANDAG), Southern
                    California Association of Governments (SCAG)
                </li>
            </ul>
            <p>MPO Data Outreach and Acquisition</p>

            <p>
                In April, 2023, CBI conducted outreach to multiple Metropolitan Planning Organizations (MPO) on behalf
                of the Office of Land Use and Climate Innovation (LCI) to solicit for and acquire updated residential
                vehicle miles traveled (VMT) datasets based on their respective regional travel demand models. Requested
                geospatial data (as shapefile or feature class) included metrics for VMT per capita (by traffic analysis
                zone [TAZ]), VMT per capita (for region), date layer was created, and model output year. Based on that
                outreach, data of varying completeness was received from BCAG, FCOG, SACOG, SANDAG, SCAG, and SLOCOG.
                After quality control/quality assurance (QAQC) review, only datasets from BCAG, FCOG, and SACOG were
                included in this Version 2.1 update (see note below on data exclusions for more detail).
            </p>

            <p>MPO Data Aggregation & Attribute Updates</p>

            <p>
                Data that passed a QAQC check was aggregated from individual MPO's regional transit demand model output
                datasets and used to provide residential VMT per capita estimates throughout the area of each respective
                MPO's jurisdictional boundary. This data was projected into NAD 83 California Teale Albers (EPSG: 3310)
                and appended to the existing Version 2.0 VMT per capita dataset. A query field (QUERY) was added to
                denote new data acquired for Version 2.1 (2023 - New Data), existing data to be included in Version 2.1
                (2023 - Prior Data), and existing data to be excluded from Version 2.1 (2023 - Exclude). The exclusion
                category was used to set a definition query to filter out older data that was not used in Version 2.1
                (i.e., BCAG, FCOG, SACOG, CSTDM 2016). A date field (SiteCheck) was added to track the year in which the
                data was added and used to update Site Check (e.g., 2023).
            </p>

            <p>Updates to California Statewide Travel Demand Model (CSTDM) Data</p>

            <p>
                Versions 1.0 and 2.0 of the VMT per capita dataset used output data from the California Department of
                Transportation's (CalTrans) 2016 CSTDM to fill in all areas without MPO-derived VMT per capita coverage.
                Based on subsequent conversations with LCI (May 2023), Version 2.0 of the VMT per capita dataset
                replaced use of the 2016 CSTDM in favor of the more recent 2020 CSTDM and adopted a revised ruleset for
                aggregating MPO regional travel model data with the CSTDM.
            </p>

            <p>
                In Version 2.1, MPO datasets (when available) were the authoritative source for VMT per capita estimates
                within the entire jurisdictional boundary of each respective MPO, and therefore all data across an MPO
                comes from a single source: their regional travel demand model. For areas with no MPO and no regional
                travel demand model, VMT per capita estimates were instead sourced from CalTran's 2020 CSTDM. Within the
                2020 CSTDM, regional VMT per capita (regional average) is calculated in either of two ways. For areas
                within an MPO, the regional average is based on the total VMT and total population across the MPO's
                jurisdictional boundary. For areas outside of an MPO, the regional average is based on the total VMT and
                total population across the county. These values were calculated by CalTrans and left unaltered for
                inclusion in Version 2.1 of the VMT per capita dataset.
            </p>

            <p>Calculation of Below Regional Average VMT per Capita</p>

            <p>
                VMT is a model output (metric) that describes travel behavior for every resident as the number of miles
                traveled on a typical weekday when school is in session (CalTrans). Total VMT (sum) and total
                residential population (sum) are calculated by a regional or statewide travel demand model and
                summarized by Traffic Analysis Zone (TAZ), which are discrete geographic units used for both modeling
                and reporting. Dividing total VMT by total population within each TAZ produces the metric of residential
                VMT per capita. Similarly, dividing total VMT by total population across an entire region produces the
                metric of regional VMT per capita (i.e., regional average). Dividing the residential VMT per capita
                within each TAZ by the regional average and multiplying by 100 produces the metric called percent VMT. A
                residential VMT per capita that equals the regional average has a percent VMT of 100%. Areas with a
                residential VMT per capita that can be considered less than or equal to 15% below average have a percent
                VMT ranging from 85% to 99%. Within the VMT per capita dataset, all areas that fall within this range
                were denoted as such in the VMT Category field as Below Average, and then extracted as a separate
                feature class representing this VMT category solely.
            </p>

            <p>Treatment of No Data and Anomalous Data</p>

            <p>
                Areas where regional or statewide models could not produce reliable estimates and reported as No Data
                are left unaltered. MPO data based on regional models that reported numerous residential VMT per capita
                estimates higher than 100 were flagged as anomalous, and data for the region was instead sourced from
                the 2020 CSTDM.
            </p>

            <p>Special Note on Excluded Data</p>

            <p>
                New VMT datasets were initially received from San Diego Association of Governments (SANDAG), Southern
                California Association of Governments (SCAG), and San Luis Obispo Council of Governments (SLOCOG). Per
                California Office of Land Use and Climate Innovation (LCI), these datasets did not pass quality QAQC inspection
                based on unexplained missing data as well as anomalous data for VMT per capita values greater than 100,
                and as such were excluded from the Version 2.1 update. Existing MPO data from Version 1.0 and Version
                2.0 was instead used for SANDAG and SCAG, while SLOCOG was replaced with CSTDM (2020) data.
            </p>

            <p>Full List of MPO Outreach (April 2023)</p>

            <p>
                Association of Monterey Governments (AMBAG), Butte County Association of Governments (BCAG), Kings
                County Association of Governments (KCAG), Fresno Council of Governments (FCOG), Kern Council of
                Governments (KCOG), Merced Association of Governments (MCAG), Madera County Transportation Commission
                (MCTC), Metropolitan Transportation Commission (MTC), Sacramento Area Council of Governments (SACOG),
                San Diego Association of Governments (SANDAG), San Luis Obispo Council of Governments (SLOCOG), Santa
                Barbara County Association of Governments (SBCAG), Shasta Regional Transportation Agency (SRTA),
                Southern California Association of Governments (SCAG), Stanislaus Council of Governments (SCOG), Tulare
                County Association of Governments (TCOG), Tahoe Regional Planning Agency (TRPA)
            </p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>Parcels selected by intersecting with VMT polygons</li>
                <li>Intersecting parcels populated with "0"</li>
                <li>Non-intersecting parcels populated with "1"</li>
            </ol>

            <h4 className="title is-4">Wetlands [8.1]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>California Aquatic Resource Inventory Wetland Polygons</p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>Parcels selected by intersecting with wetlands polygons</li>
                <li>Intersecting parcels populated with "0"</li>
                <li>Non-intersecting parcels populated with "1"</li>
            </ol>

            <h4 className="title is-4">Riparian Areas [8.2]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>DWR Natural Communities Commonly Associated with Groundwater; Vegetation</p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>Parcels selected by intersecting with riparian area polygons</li>
                <li>Intersecting parcels populated with "0"</li>
                <li>Non-intersecting parcels populated with "1"</li>
            </ol>

            <h4 className="title is-4">Habitats [8.3]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>CA Statewide Wildlife Action Plan</p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>Parcels selected by intersecting with special habitats polygons</li>
                <li>Intersecting parcels populated with "0"</li>
                <li>Non-intersecting parcels populated with "1"</li>
            </ol>

            <p>Notes: Best proxy for special habitats for now and will use better data once it's available.</p>

            <h4 className="title is-4">Rare, Threatened or Endangered Species [8.5]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <ol className="is-indented">
                <li>CNDDB State Listed Species (polygons)</li>
                <li>USFWS Threatened & Endangered Species Active Critical Habitat</li>
                <li>NLCD 2016 Percent impervious</li>
            </ol>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>Extract the year from the ELMDATE field in the CNDDB</li>
                <li>
                    <div>Select CNDDB polygons that meet the following criteria:</div>
                    <div>
                        ("FEDLIST" in ('Threatened', 'Endangered') OR "CALLIST" in ('Rare', 'Threatened', 'Endangered'))
                        AND YEAR_CALC &gt; 1995 AND TAXONGROUP not in ('Fish', 'Crustaceans' , 'Inland Waters' ,
                        'Mollusks')
                    </div>
                </li>
                <li>Clip USFWS data to the state of California.</li>
                <li>
                    <div>
                        Select species groups of interest from USFWS Threatened & Endangered Species Active Critical
                        Habitat. (See code definitions{' '}
                        <a
                            href="https://my.usgs.gov/confluence/pages/viewpage.action?pageId=518426757"
                            target="_blank"
                            rel="noreferrer"
                        >
                            here
                        </a>
                        )
                    </div>
                    <div>
                        "spcode" NOT like 'E%' AND "spcode" NOT like 'K%' AND "spcode" NOT like 'N%' AND "spcode" NOT
                        like 'O%' AND "spcode" NOT like 'P%' AND "spcode" NOT like 'F%' AND "spcode" NOT like 'V%' AND
                        "spcode" NOT like 'W%'AND status = 'Final'
                    </div>
                </li>
                <li>Union selected CNDDB and selected USFWS polygons.</li>
                <li>
                    Reclassify NLCD 2016 Impervious Surfaces to convert all pixels with &gt; 0% development to a 1;
                    other values to NoData.
                </li>
                <li>Convert NLCD &gt; 0% to Vector</li>
                <li>Erase NLCD &gt; 0% from the unioned CNDDB and USFWS</li>
                <li>Select parcels intersecting the areas identified above.</li>
                <li>Intersecting parcels populated with "0"</li>
                <li>Non-intersecting parcels populated with "1"</li>
            </ol>

            <p>
                Note: If a parcel INTERSECTS (i.e., partially overlaps) a CNDDB polygon meeting a set of criteria
                (listed below), it is flagged as not meeting the "Rare, Threatened or Endangered Species" requirement.
                We did not conduct any distance based selections that would flag a parcel that is 100-ft away from one
                of these polygons.
            </p>

            <p>Criteria used to select CNDDB polygons:</p>
            <ul className="bullets">
                <li>Federally threatened or endangered listed species</li>
                <li>California rare, threatened, or endangered listed species</li>
                <li>Last observed after 1995</li>
                <li>And not present in one of these categories: 'Fish', 'Crustaceans', 'Inland Waters', 'Mollusks'</li>
                <li>We also removed portions of the CNDDB polygons that overlapped with NLCD impervious surfaces.</li>
            </ul>

            <h4 className="title is-4">Prime Farmlands or Farmlands of Statewide Importance [8.6]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>California Important Farmland (FMMP 2020)</p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>Create a subset of California Important Farmland features with code = P or S.</li>
                <li>Select parcels that intersect the subset features.</li>
                <li>Intersecting parcels populated with a value of 0.</li>
                <li>Non-intersecting parcels populated with a value of 1.</li>
            </ol>

            <h4 className="title is-4">Earthquake/Seismic Fault Zones [9.2]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>Alquist-Priolo Earthquake Fault Zones (California Geological Survey 2022)</p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>Project input into NAD 1983 California (Teale) Albers (Meters) [EPSG 3310].</li>
                <li>Parcels selected by intersecting with Alquist-Priolo Earthquake Fault Zones.</li>
                <li>Intersecting parcels are populated with a value of 0.</li>
                <li>Non-intersecting parcels are populated with a value of 1.</li>
            </ol>

            <p>
                <u>Source:</u>
            </p>
            <p>CGS Alquist-Priolo Fault Zones</p>
            <p>
                <a href="https://cadoc.maps.arcgis.com/home/item.html?id=29d2f0e222924896833b69ff1b6d2ca3">
                    https://cadoc.maps.arcgis.com/home/item.html?id=29d2f0e222924896833b69ff1b6d2ca3
                </a>
            </p>

            <h4 className="title is-4">Wildfire Hazard [9.3]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>CAL FIRE FRAP Fire Hazard Severity Zones</p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>
                    Reclassify wildfire hazard raster to keep only the following threat level classes: High and Very
                    High
                </li>
                <li>Perform zonal statistics to count the number of pixels in the above classes within each parcel</li>
                <li>If the count == 0, requirement met (field populated with 1)</li>
                <li>If the count &gt; 0, requirement not met (field populated with 0)</li>
            </ol>

            <h4 className="title is-4">Flood Plain [9.4]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>National Flood Hazard Layer (NFHL) for CA from FEMA</p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>
                    <div>Classes Selected:</div>
                    <div>'A', 'AE', 'AH', 'AO', 'A99', 'VE'</div>
                    <div>
                        The selected classes are based on the 100 year floodplain map for{' '}
                        <a
                            href="https://waterresources.saccounty.net/stormready/PublishingImages/100-Year-Floodplain-Map-large.pdf"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Sacramento County
                        </a>
                    </div>
                    <div>Class 'VE' was also included to account for coastal counties</div>
                </li>
                <li>Select Parcels that intersect these classes.</li>
                <li>Selected parcels populated with "0"</li>
                <li>Unselected parcels populated with "1"</li>
            </ol>

            <h4 className="title is-4">Landslide Hazard [9.5]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>Landslide Hazard Zones (California Geological Survey 2021)</p>
            <p>Liquefaction Zones (California Geological Survey 2021)</p>
            <p>Liquefaction and Landslide Zones Overlap (California Geological Survey 2021)</p>

            <p>
                <u>Methods:</u>
            </p>
            <p>Creating the Composite Landslide Hazard Zone Dataset:</p>
            <ol className="is-indented">
                <li>Project all inputs into NAD 1983 California (Teale) Albers (Meters) [EPSG 3310].</li>
                <li>Merge all inputs into a single dataset with common attributes.</li>
                <li>Add a short integer field called "gridcode" and populate with a value of 1.</li>
            </ol>
            <p>Assigning Parcel Eligibility under Requirement 9.5:</p>
            <ol className="is-indented">
                <li>Convert vector to raster using gridcode value and a cell size 10 meters.</li>
                <li>Calculate the count of pixels within a landslide hazard zone for every parcel.</li>
                <li>Calculate the area of landslide hazard zone pixels within every parcel.</li>
                <li>Calculate the area of each parcel in the same units.</li>
                <li>Divide parcel landslide hazard area by total parcel area.</li>
                <li>
                    If the ratio of landslide hazard area to parcel area is greater than 20%, the parcel is populated
                    with a value of 0.
                </li>
                <li>If the ratio is less than 20%, the parcel is populated with a value of 1.</li>
            </ol>

            <p>
                <u>Sources:</u>
            </p>
            <p>CGS Landslide Zones:</p>
            <p>
                <a href="https://cadoc.maps.arcgis.com/home/item.html?id=921a7c243d074fa081af2b9d19be0315">
                    https://cadoc.maps.arcgis.com/home/item.html?id=921a7c243d074fa081af2b9d19be0315
                </a>
            </p>
            <p>CGS Liquefaction: </p>
            <p>
                <a href="https://cadoc.maps.arcgis.com/home/item.html?id=fcf1ebfe19be49819a12c404d74b4a7d">
                    https://cadoc.maps.arcgis.com/home/item.html?id=fcf1ebfe19be49819a12c404d74b4a7d
                </a>
            </p>
            <p>CGS Landslide and Liquefaction Overlap:</p>
            <p>
                <a href="https://cadoc.maps.arcgis.com/home/item.html?id=f16470d0f27f464ea14f81cbad1741fe">
                    https://cadoc.maps.arcgis.com/home/item.html?id=f16470d0f27f464ea14f81cbad1741fe
                </a>
            </p>

            <h4 className="title is-4">State Conservancy [9.6]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>State of California Conservancies</p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>Select parcels that "INTERSECT" State of California Conservancies boundaries</li>
                <li>Selected parcels populated with "0"</li>
                <li>Unselected parcels populated with "1"</li>
            </ol>

            <h4 className="title is-4">Protected Areas [9.8]</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>Protected Area Mask for Development Exclusion</p>

            <p>
                <u>Methods:</u>
            </p>
            <ol className="is-indented">
                <li>Select parcels that "INTERSECT" Protected Area Mask for Development Exclusion</li>
                <li>Selected parcels populated with "0"</li>
                <li>Unselected parcels populated with "1"</li>
            </ol>

            <h4 className="title is-4">LCI Land Use Categories</h4>
            <p>
                <u>Inputs:</u>
            </p>
            <p>California Statewide Zoning Data</p>

            <p>
                <u>Methods:</u>
            </p>

            <p>
                LCI staff interpreted statewide zoning data collected to categorize parcels into thirteen general land
                use categories for the purposes of displaying land use information in Site Check. The general land use
                categories in the tool were borrowed from a 2004 study of general plans in California{' '}
                <a href="https://databasin.org/datasets/1cda3056a4ad4ece86eb5eda4ef17e82/">(more information here)</a>.
            </p>
            <p>
                <u>Notes:</u>
            </p>

            <ul className="is-indented">
                <li>
                    The "Planned Area" category includes parcels zoned as "Urban Reserve" category for visual clarity in
                    the tool's interactive map.{' '}
                </li>
                <li>All users should independently confirm land use designations associated with parcels. </li>
            </ul>
            <p>California Statewide Zoning Data</p>

            <p>
                This data is provided as a public service, for informational purposes only. This data should not be
                construed as legal advice. Users of this data should independently verify its determinations prior to
                taking any action under the California Environmental Quality Act (CEQA) or any other law. The State of
                California makes no warranties as to the accuracy of this data.
            </p>
            <p>
                This layer contains zoning data collected from 535 of California's 539 jurisdictions. An effort was made
                to contact each jurisdiction in the state and request zoning data in whatever form available. In the
                event that zoning maps were not available in a GIS format, maps were converted from PDF or image maps
                using geo-referencing techniques and then transposing map information to parcel geometries sourced from
                county assessor data. Collection efforts began in late 2021 and were mostly finished in late 2022. Some
                data has been updated in 2023. Sources and dates are documented in the "Source" and "Date" columns with
                more detail available in the accompanying sources table.{' '}
            </p>
            <p>
                Individual zoning maps were combined for this statewide dataset. As part of the aggregation process,
                contiguous areas with identical zone codes, within jurisdictions, were merged or dissolved. Some
                features representing roads with right-of-way or Null zone designations were removed from this data.
                Features less than 4 square meters in area were also removed.
            </p>
            <p>
                <u>Source:</u>
            </p>
            <p>California Statewide Zoning Data</p>
            <p>
                <a href="https://gis.data.ca.gov/datasets/Gov-OPR::california-statewide-zoning/about">
                    https://gis.data.ca.gov/datasets/Gov-OPR::california-statewide-zoning/about
                </a>
            </p>
        </div>
    </Modal>
)
